"use client"
import { iconsMap } from "@/public/icons/iconsMap"
import { getNonUmbracoUrl } from "@/utils"
import styled from "@emotion/styled"
import { Box, ButtonBase, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useState } from "react"
import "swiper/css"
import "swiper/css/navigation"
import { Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { colors, mediaQueries, sizes, useIsDesktop, useIsTabletOnly } from "ui"
import { getArrowIcon } from "utils/getArrowIcon"
import { transformProperties } from "utils/simplifyData"
import EnhancedTypography from "./bit/EnhancedTypography"

const StyledSwiper = styled(Swiper, {
  shouldForwardProp: (prop) => prop !== "activeSwiper",
})<{ activeSwiper?: boolean }>`
  position: relative;
  justify-content: center;
  display: flex;
  align-items: center;
  padding: 0;
  overflow-y: visible;
  .swiper-wrapper {
    align-items: stretch;
    height: auto !important;
    overflow-y: visible;
    ${mediaQueries.desktop} {
      justify-content: ${(props) =>
        props.activeSwiper ? "flex-start" : "unset"};
    }
  }
`
const StyledSwiperSlide = styled(SwiperSlide, {
  shouldForwardProp: (prop) => prop !== "primaryColor",
})<{ primaryColor?: string }>`
  border-radius: ${sizes.baseDouble};
  background: rgba(255, 255, 255, 0.8);
  box-shadow: -9.772px -9.772px 36.646px 0px rgba(15, 36, 79, 0.08);
  backdrop-filter: blur(20.76602554321289px);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${sizes.baseTriple};
  height: 100%;
  min-height: fit-content;
  cursor: pointer;
  border: 1px solid transparent;
  &:hover {
    border: 1px solid ${(props) => props.primaryColor || colors.lightOrange};
  }
  .card-link {
    gap: 13px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-decoration: none;
    height: 100%;
  }

  ${mediaQueries.desktopM} {
    .card-link {
      gap: ${sizes.baseSingle};
      justify-content: space-between;
      height: 100%;
    }
  }
  ${mediaQueries.desktopXl} {
    .card-link {
      gap: ${sizes.baseDouble};
      justify-content: space-between;
      height: 100%;
    }
  }
`

const StyledImageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bgcolor",
})<{ bgcolor?: string }>`
  width: 48px;
  height: 48px;
  background-color: ${(props) => props.bgcolor};
  padding: ${(props) => (props.bgcolor ? "10px" : "0")};
  border-radius: ${sizes.baseSingle};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`
const StyledImageWrapper = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const StyledArrowContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isVisible",
})<{ isVisible: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 36px;
  margin-bottom: ${sizes.baseQuadric};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  visibility: ${(props) => (props.isVisible ? "visible" : "hidden")};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
  ${mediaQueries.desktopM} {
    margin-bottom: 0;
  }
`

const StyledArrowIconWrapper = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  z-index: 1;
  cursor: pointer;
  &.swiper-button-prev-a {
    transform: rotate(180deg);
  }
  svg {
    width: 48px;
    height: 48px;
    ${mediaQueries.mobile} {
      width: 28px;
      height: 28px;
    }
    ${mediaQueries.desktopXl} {
      width: 52px;
      height: 52px;
    }
  }
`
const StyledTextContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  h3 {
    font-size: 16px;
    ${mediaQueries.mobile} {
      font-size: 19px;
    }
  }
`
const StyledText = styled(EnhancedTypography, {
  shouldForwardProp: (prop) => prop !== "isArrowIcon",
})<{
  isArrowIcon?: boolean
}>`
  text-align: center;
  margin-top: ${(props) => (props.isArrowIcon ? "0" : "auto")};
  font-size: 16px;
  ${mediaQueries.mobile} {
    font-size: 14px;
  }
`
const StyledArrowWrapper = styled(Box)`
  display: flex;
  height: 24px;
  width: 24px;
  margin-top: auto;
`
interface SwiperCardBoxesContentProps {
  itemsList: any
  showArrowsNavigation?: boolean
  desktopSlidesPerView?: number
  onItemClick?: (item: any) => void
}
const SwiperCardBoxesContent = ({
  itemsList,
  showArrowsNavigation = true,
  desktopSlidesPerView = 4,
  onItemClick,
}: SwiperCardBoxesContentProps) => {
  const isDesktop = useIsDesktop()
  const isTablet = useIsTabletOnly()
  const NextPrevArrowIcon = iconsMap["arrow_lightOrange_black_line"]
  const numSlidersPerView = isTablet ? 2 : isDesktop ? desktopSlidesPerView : 1 // tablet: 2, desktop: according to desktopSlidesPerView, mobile: 1 per view
  const currentList = itemsList?.blocks || itemsList
  // Add state to control client-side rendering
  const [isMounted, setIsMounted] = useState(false)

  // Set mounted state after hydration
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Determine if navigation arrows should be visible
  const shouldShowNavigation =
    isMounted && showArrowsNavigation && currentList?.length > numSlidersPerView
  // Handle case where currentList is undefined or empty
  if (!currentList || currentList.length === 0) {
    return null // Return nothing if no items to display
  }

  return (
    <>
      <StyledSwiper
        navigation={{
          nextEl: ".swiper-button-next-a",
          prevEl: ".swiper-button-prev-a",
        }}
        spaceBetween={isDesktop ? 30 : 40}
        modules={[Navigation]}
        autoHeight
        loop={isDesktop ? false : true}
        // centeredSlides={isDesktop ? false : true}
        slidesPerView={numSlidersPerView}
        centerInsufficientSlides
        watchOverflow
        initialSlide={0}
        observer
        onInit={(swiper) => {
          setTimeout(() => {
            if (
              swiper.navigation &&
              typeof swiper.navigation.update === "function"
            ) {
              swiper.navigation.update()
            }
          }, 100) // Delay to ensure swiper is fully initialized
        }}
      >
        {currentList?.map((item: any, index: number) => {
          const currItem = item?.properties
            ? transformProperties(item?.properties)
            : item
          const { primaryColor, link, icon, title, text } = currItem
          const currPrimaryColor = `#${primaryColor?.value || "00A7B5"}`
          const ArrowIcon = getArrowIcon(currPrimaryColor)
          const linkUrl = link?.links?.[0]?.url || ""
          const linkTarget = link?.links?.[0]?.target
          // Generate a truly unique key
          const uniqueKey = `card-${item?.id || ""}-${index}-${Date.now()}`
          const t = useTranslations("SEO")
          const LinkComponent = linkUrl ? Link : Box

          // Get the image URL safely with proper fallbacks
          const iconUrl =
            icon?.mediaItems && icon.mediaItems.length > 0
              ? icon.mediaItems[0]?.url
              : icon?.src || ""

          return (
            <StyledSwiperSlide key={uniqueKey} primaryColor={currPrimaryColor}>
              <LinkComponent
                href={getNonUmbracoUrl(linkUrl)}
                target={linkTarget}
                className="card-link"
                onClick={() => onItemClick?.(item)}
              >
                <StyledImageContainer
                  bgcolor={
                    primaryColor?.value ? "#" + primaryColor?.value : undefined
                  }
                >
                  {iconUrl && (
                    <StyledImageWrapper>
                      <Image
                        src={iconUrl}
                        alt={`${t("iconAlt")}${title?.value || ""}`}
                        fill
                      />
                    </StyledImageWrapper>
                  )}
                </StyledImageContainer>
                <StyledTextContainer>
                  {(title?.value || typeof title === "string") && (
                    <Typography
                      variant="h5"
                      component="h3"
                      color={colors.black}
                    >
                      {title?.value || title}
                    </Typography>
                  )}
                  {(text?.value || typeof text === "string") && (
                    <StyledText
                      variant="body1"
                      component="p"
                      color={colors.red}
                      isArrowIcon={!!ArrowIcon && !!linkUrl}
                    >
                      {text?.value || text}
                    </StyledText>
                  )}
                </StyledTextContainer>
                {ArrowIcon && linkUrl && (
                  <StyledArrowWrapper>
                    {isDesktop && <ArrowIcon />}
                  </StyledArrowWrapper>
                )}
              </LinkComponent>
            </StyledSwiperSlide>
          )
        })}
      </StyledSwiper>
      <StyledArrowContainer isVisible={shouldShowNavigation}>
        <StyledArrowIconWrapper className="swiper-button-prev-a">
          <NextPrevArrowIcon />
        </StyledArrowIconWrapper>
        <StyledArrowIconWrapper className="swiper-button-next-a">
          <NextPrevArrowIcon />
        </StyledArrowIconWrapper>
      </StyledArrowContainer>
    </>
  )
}

export default SwiperCardBoxesContent
